import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Minus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getUserFriendlyError } from "@/lib/errors";

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  free_shipping?: boolean;
  stripe_price_id?: string | null;
}

interface ProductQuantityControlProps {
  product: Product;
  onCartUpdate?: () => void;
}

const ProductQuantityControl = ({ product, onCartUpdate }: ProductQuantityControlProps) => {
  const [quantity, setQuantity] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    updateQuantityFromCart();
    
    const handleCartUpdate = () => {
      updateQuantityFromCart();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, [product.id]);

  const updateQuantityFromCart = () => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const cartItem = cart.find((item: any) => item.product.id === product.id);
      setQuantity(cartItem?.quantity || 0);
    } catch (error) {
      setQuantity(0);
    }
  };

  const updateCart = (newQuantity: number) => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const existingIndex = cart.findIndex((item: any) => item.product.id === product.id);

      if (newQuantity <= 0) {
        // Remove from cart
        if (existingIndex >= 0) {
          cart.splice(existingIndex, 1);
          toast({
            title: "Verwijderd uit winkelwagen",
            description: `${product.name} is verwijderd`
          });
        }
      } else if (existingIndex >= 0) {
        // Update quantity
        cart[existingIndex].quantity = newQuantity;
      } else {
        // Add to cart
        cart.push({
          id: `cart_${Date.now()}`,
          quantity: newQuantity,
          product: {
            id: product.id,
            name: product.name,
            description: product.description,
            price: product.price,
            image_url: product.image_url,
            free_shipping: product.free_shipping || false,
            stripe_price_id: product.stripe_price_id || null
          }
        });
        toast({
          title: "Toegevoegd aan winkelwagen",
          description: `${product.name} is toegevoegd`
        });
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
      setQuantity(newQuantity);
      onCartUpdate?.();
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Winkelwagen bijwerken"),
        variant: "destructive"
      });
    }
  };

  const increment = () => updateCart(quantity + 1);
  const decrement = () => updateCart(quantity - 1);

  if (quantity === 0) {
    return (
      <Button
        onClick={increment}
        size="sm"
        variant="outline"
        className="border-foreground text-foreground hover:bg-foreground hover:text-background rounded-full text-xs uppercase tracking-wider font-semibold px-6"
      >
        <Plus className="mr-1 h-4 w-4" />
        Toevoegen
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={decrement}
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-full border-foreground text-foreground hover:bg-foreground hover:text-background"
        aria-label="Verlaag aantal"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <span className="w-8 text-center font-bold text-foreground tabular-nums">
        {quantity}
      </span>
      <Button
        onClick={increment}
        size="icon"
        variant="outline"
        className="h-8 w-8 rounded-full border-foreground text-foreground hover:bg-foreground hover:text-background"
        aria-label="Verhoog aantal"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default ProductQuantityControl;
